import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, ImageIcon, FileText } from "lucide-react";
import { toast } from "sonner";

export function PrivateImage({ path, className, alt = "" }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) { setSrc(null); return undefined; }
    let url = null;
    let alive = true;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => { if (alive) { url = URL.createObjectURL(r.data); setSrc(url); } })
      .catch(() => setSrc(null));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [path]);
  return src ? <img src={src} alt={alt} className={className} /> : <div className={`${className} bg-[#F2EEE5]`} />;
}

export function ImageUpload({ value, onChange, testid = "image-upload-input", accept = "image/*", fileMode = false, isPrivate = false }) {
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" }, params: isPrivate ? { private: true } : undefined });
      onChange(data.path);
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const preview = isPrivate
    ? <PrivateImage path={value} alt="preview" className="h-full w-full object-cover" />
    : <img src={fileUrl(value)} alt="preview" className="h-full w-full object-cover" />;

  return (
    <div className="flex items-center gap-4">
      <div className="h-20 w-20 rounded-lg border border-[#E2DAD0] bg-[#F2EEE5] overflow-hidden flex items-center justify-center shrink-0">
        {value ? (
          fileMode ? <FileText className="h-7 w-7 text-[#194324]" /> : preview
        ) : (
          fileMode ? <FileText className="h-6 w-6 text-[#9CA99F]" /> : <ImageIcon className="h-6 w-6 text-[#9CA99F]" />
        )}
      </div>
      <label className="cursor-pointer">
        <input type="file" accept={accept} className="hidden" onChange={handle} data-testid={testid} />
        <Button type="button" variant="outline" asChild>
          <span>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {value ? "Replace" : fileMode ? "Upload File" : "Upload Image"}
          </span>
        </Button>
      </label>
    </div>
  );
}
